import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LogoutIcon from '@mui/icons-material/Logout';
import SearchIcon from '@mui/icons-material/Search';
import LockResetIcon from '@mui/icons-material/LockReset';
import { Box, CircularProgress, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import styled from 'styled-components';
import { IMenuItens } from '.';
import { history } from '../../helpers/history';
import { authAtom } from '../../states/usuarios/AuthState';
import LanguageSelector from '../LanguageSelector';
import { useTranslation } from '../../commons/useTranslasion';
import { useRedefinirSenhaActions } from '../../states/redefinirSenha/redefinirSenha.actions';
import { toast } from 'react-toastify';
import CollapseMenu from './CollapseMenu';

interface MenuHorizontalProps {
  menuItens: IMenuItens[];
}

const LinkMenu = styled(Link)`
  font-size: 14px;
  font-weight: 400;
  text-decoration: none;
  padding: 8px 16px;
  display: flex;
  color: #000;
  transition: 0.3s;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const LiMenu = styled.li`
  margin-bottom: 2px;
`;

const MenuHorizontal: React.FC<MenuHorizontalProps> = ({ menuItens }) => {
  const { language, translate } = useTranslation();
  const setAuth = useSetRecoilState(authAtom);
  const auth = useRecoilValue(authAtom);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});
  
  // Função para alternar a expansão de um menu
  const toggleMenu = (path: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  // Função recursiva para renderizar os itens de menu
  const renderMenuItens = (menuItens: IMenuItens[], isFirstLevel = true, parentPath = '') => {
    return menuItens.map((menu, index) => {
      const menuPath = parentPath ? `${parentPath}.${index}` : `${index}`;
      const isExpanded = !!expandedMenus[menuPath];
      
      return (
        <LiMenu key={index}>
          <LinkMenu
            to={menu.link}
            onClick={(e) => {
              if (menu.filhos && menu.filhos.length > 0) {
                e.preventDefault();
                toggleMenu(menuPath);
              }
            }}
            style={{
              fontWeight: menu.filhos && menu.filhos.length > 0 ? 500 : 400,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: isExpanded ? '#f0f0f0' : 'transparent',
              paddingLeft: isFirstLevel ? '5px' : '16px'
            }}
          >
            <span>{`${isFirstLevel ? '': `${index + 1}.`} ${menu.nome}`}</span>
            {menu.filhos && menu.filhos.length > 0 && (
              <span>
                {isExpanded ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </span>
            )}
          </LinkMenu>
          {menu.filhos && menu.filhos.length > 0 && (
            <Box
              component="ul"
              sx={{
                maxHeight: isExpanded ? '1000px' : '0',
                visibility: isExpanded ? 'visible' : 'hidden',
                overflow: 'hidden',
                listStyle: 'none',
                padding: 0,
                paddingLeft: isFirstLevel ? 0 : '16px',
                margin: 0,
                marginTop: isExpanded ? '2px' : 0,
                marginBottom: isExpanded ? '2px' : 0,
                transition: 'max-height 0.3s ease-in-out, visibility 0s' + (isExpanded ? '0s' : ' 0.3s'),
                opacity: isExpanded ? 1 : 0,
                position: 'relative',
                height: isExpanded ? 'auto' : 0,
                transitionProperty: 'max-height, opacity, margin',
                transitionDuration: '0.3s',
                transitionTimingFunction: 'ease-in-out'
              }}
            >
              {renderMenuItens(menu.filhos, false, menuPath)}
            </Box>
          )}
        </LiMenu>
      );
    });
  };

  const handleCloseSair = () => {
    localStorage.removeItem('usuario');
    setAuth(null);
    history.push('/login');
  };
  
  const redefinirSenhaActions = useRedefinirSenhaActions();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  
  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleRedefinirSenha = () => {
    if (auth && auth.email) {
      setIsLoading(true);
      setOpenModal(false);
      redefinirSenhaActions.create({ email: auth.email, origem: 'ASSESSORADO' })
        .then(() => {
          toast.success(translate('emailEnviadoComSucesso'));
        })
        .catch((error) => {
          toast.error(translate('erroAoRedefinirSenha'));
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      toast.error(translate('usuarioNaoEncontrado'));
    }
  };


  
  return (
    <nav>
      <CollapseMenu>
        <Box
          sx={{
            display: 'flex',
            gap: '8px'
          }}
        >
          <IconButton color="inherit" title={translate('pesquisar')}>
            <SearchIcon />
          </IconButton>
          <LanguageSelector />
          <IconButton color="inherit" onClick={handleOpenModal} title={translate('redefinirSenha')} disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} color="inherit" /> : <LockResetIcon />}
          </IconButton>
          <IconButton color="inherit" onClick={() => handleCloseSair()} title={translate('sair')}>
            <LogoutIcon />
          </IconButton>
        </Box>
        
        <Box
          component="ul"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            listStyle: 'none',
            padding: 0,
            margin: 0,
            gap: '8px',
            width: '100%'
          }}
        >
          {renderMenuItens(menuItens)}
        </Box>
      </CollapseMenu>
      
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {translate('confirmarRedefinicaoSenha')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {translate('mensagemConfirmacaoRedefinicaoSenha')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>{translate('fecharDialog')}</Button>
          <Button onClick={handleRedefinirSenha} autoFocus variant="contained" color="primary">
            {translate('enviar')}
          </Button>
        </DialogActions>
      </Dialog>
    </nav>
  );
};

export default MenuHorizontal;
